import { Link } from "@tanstack/react-router";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  basePath: string; // e.g., "/blog" — page URLs will be basePath/page/N/
  variant?: "light" | "dark";
}

export function Pagination({
  currentPage,
  totalPages,
  basePath,
  variant = "light",
}: PaginationProps) {
  if (totalPages <= 1) return null;

  function pageUrl(page: number): string {
    if (page === 1) return `${basePath}/`;
    return `${basePath}/page/${page}/`;
  }

  const isDark = variant === "dark";
  const textColor = isDark ? "text-eng-bright" : "text-gray-80";
  const mutedColor = isDark ? "text-eng-circuit" : "text-gray-60";
  const activeBg = isDark ? "bg-eng-bright text-eng-void" : "bg-gray-80 text-white";

  return (
    <nav
      aria-label="Pagination"
      className="mt-10 flex items-center justify-center gap-1 md:mt-28"
    >
      {currentPage > 1 ? (
        <Link
          to={pageUrl(currentPage - 1)}
          className={`pr-2 text-xs font-semibold leading-none md:text-base ${textColor}`}
        >
          Previous
        </Link>
      ) : (
        <span className={`hidden pr-2 text-xs font-semibold leading-none sm:block md:text-base ${mutedColor}`}>
          Previous
        </span>
      )}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <Link
          key={page}
          to={pageUrl(page)}
          className={`flex h-6 w-6 items-center justify-center rounded-sm text-xs font-bold md:h-7 md:w-7 md:text-base ${
            page === currentPage ? activeBg : mutedColor
          }`}
        >
          {page}
        </Link>
      ))}

      {currentPage < totalPages ? (
        <Link
          to={pageUrl(currentPage + 1)}
          className={`pl-2 text-xs font-semibold leading-none md:text-base ${textColor}`}
        >
          Next
        </Link>
      ) : (
        <span className={`hidden pl-2 text-xs font-semibold leading-none sm:block md:text-base ${mutedColor}`}>
          Next
        </span>
      )}
    </nav>
  );
}
